// IndexedDB-backed offline queue for webform submissions and photo blobs.
// Wraps `idb` with the minimum surface the useOfflineSubmit hook needs:
// enqueue, list-by-status, mark-sync-result, retry, discard.
//
// Stores:
//   - submissions: queued webform rows. Key = client_submission_id (text).
//   - photo_blobs: photo bytes for hasPhotos form-kinds. Schema declared in
//                  Phase 1B; writes wired in Phase 1D-A. Key = the
//                  deterministic bucket path including the .jpg suffix
//                  (`${form_kind}/${client_submission_id}/${photo_key}.jpg`)
//                  so the IDB key matches the storage object's key 1:1 —
//                  no transform needed at upload time.
//
// Status flow:
//   queued    → ready for the next sync attempt
//   syncing   → an attempt is in flight (lock against double-fire from
//               online event + 60s tick + manual button overlapping)
//   failed    → 3 retries exhausted; surfaces in the StuckSubmissions modal
//   (synced rows are deleted from the store, not retained — auditability
//    lives in Supabase, not the device.)
//
// Don't add a "synced" status. It is tempting to keep the row for
// "successfully sent" history, but the queue is for retry coordination,
// not telemetry. Deletion on success keeps the IDB store small and
// makes "stuckRows.length" the authoritative outstanding count.

import {openDB, type DBSchema, type IDBPDatabase} from 'idb';

export const DB_NAME = 'wcf-offline-queue';
export const DB_VERSION = 1;
export const STORE_SUBMISSIONS = 'submissions';
export const STORE_PHOTO_BLOBS = 'photo_blobs';
export const MAX_RETRIES = 3;

// The status vocabulary, spelled out because it IS the flow documented above.
// 'needs_attention' is the cattle_log parallel of 'failed' (see the cattle_log
// section at the bottom); listQueued/listStuck index on these exact strings.
export type SubmissionStatus = 'queued' | 'syncing' | 'failed' | 'needs_attention';

/**
 * One queued submission row.
 *
 * `payload` and `record` are `unknown` on purpose. This store is form-kind
 * agnostic — the flat forms persist a table row, the RPC forms persist a
 * {rpc, args} call, and cattle_log persists `null` and rebuilds its call from
 * `payload` — so there is no single shape to declare, and declaring one would
 * be a claim the store cannot enforce about data it hands back after a browser
 * restart. Each consumer re-proves the shape it needs (see
 * offlineRpcForms.isRpcRequest).
 */
export type QueuedSubmission = {
  csid: string;
  form_kind: string;
  payload: unknown;
  record: unknown;
  status: SubmissionStatus;
  retry_count: number;
  last_error: string | null;
  created_at: number;
  last_attempt_at: number | null;
  // Additive flat-report replay identity. Missing means legacy/another queue
  // family; never infer an actor from a team-member display name.
  actor_profile_id?: string;
  replaces_csid?: string;
  replacement_csid?: string;
  // ── cattle_log-only fields (see the create-only extensions below) ──
  errorClass?: string | null;
  errorMessage?: string | null;
  uploadedPaths?: string[];
};

/**
 * One persisted photo/attachment. `key` is the deterministic bucket path
 * INCLUDING its suffix, so the IDB key matches the storage object's key 1:1.
 */
export type PhotoBlobRow = {
  key: string;
  csid: string;
  form_kind: string;
  photo_key: string;
  blob: Blob;
  mime: string;
  size_bytes: number;
  name: string | null;
  captured_at: string;
};

// The schema `idb` type-checks every store name, key and index against. It is
// the same declaration the upgrade() callback below builds at runtime, which is
// the point: a store or index added in one and not the other is a compile
// error rather than a runtime "no such index" in the field.
interface OfflineQueueSchema extends DBSchema {
  submissions: {
    key: string;
    value: QueuedSubmission;
    indexes: {
      by_form_kind: string;
      by_status: string;
      by_form_kind_status: [string, string];
    };
  };
  photo_blobs: {
    key: string;
    value: PhotoBlobRow;
    indexes: {
      by_csid: string;
      by_form_kind: string;
    };
  };
}

// One prepared photo as dailyPhotos/prepareTaskRequestPhoto hands it over. The
// three fields the enqueue paths REQUIRE are required here; the rest carry the
// `?? default` fallbacks written at each write site.
export type PreparedPhoto = {
  photo_key: string;
  path: string;
  blob: Blob;
  mime?: string;
  size_bytes?: number;
  name?: string | null;
  captured_at?: string;
};

let dbPromise: Promise<IDBPDatabase<OfflineQueueSchema>> | null = null;

export function getDb(): Promise<IDBPDatabase<OfflineQueueSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineQueueSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_SUBMISSIONS)) {
          const s = db.createObjectStore(STORE_SUBMISSIONS, {keyPath: 'csid'});
          s.createIndex('by_form_kind', 'form_kind', {unique: false});
          s.createIndex('by_status', 'status', {unique: false});
          s.createIndex('by_form_kind_status', ['form_kind', 'status'], {unique: false});
        }
        if (!db.objectStoreNames.contains(STORE_PHOTO_BLOBS)) {
          const p = db.createObjectStore(STORE_PHOTO_BLOBS, {keyPath: 'key'});
          p.createIndex('by_csid', 'csid', {unique: false});
          p.createIndex('by_form_kind', 'form_kind', {unique: false});
        }
      },
      // Yield the connection when something else needs exclusive access to the
      // database: a deleteDatabase() (the browser-test wipe) or a schema
      // upgrade opened in another tab. This cached connection lives for the
      // page lifetime, so without closing on the versionchange it blocks that
      // operation indefinitely — and because IndexedDB serializes a delete
      // ahead of any later open, a subsequent indexedDB.open() then hangs
      // behind the pending delete (the offline_queue_canary readQueue hang).
      // Close and drop the cache so the blocked op completes deterministically;
      // the next getDb() transparently reopens. No queued data is lost — the
      // object store persists, and app code never issues a delete.
      blocking() {
        const closing = dbPromise;
        dbPromise = null;
        if (closing) {
          closing.then((db) => db.close()).catch(() => {});
        }
      },
      // The browser force-closed our connection (e.g. storage eviction). Drop
      // the cache so the next access reopens instead of using a dead handle.
      terminated() {
        dbPromise = null;
      },
    });
  }
  return dbPromise;
}

// Test-only — release the cached connection so a fake-indexeddb reset
// between tests gets picked up. Not exported through the index.
export function _resetDbForTests(): void {
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {});
  }
  dbPromise = null;
}

/**
 * Enqueue a new submission. Returns the persisted entry.
 *
 *   formKind — registry key (offlineForms)
 *   csid     — client_submission_id
 *   payload  — original form payload (for re-render)
 *   record   — the row to upsert (id + csid baked in)
 */
export async function enqueueSubmission({
  formKind,
  csid,
  payload,
  record,
  actorProfileId,
  replacesCsid,
}: {
  formKind: string;
  csid: string;
  payload?: unknown;
  record: unknown;
  actorProfileId?: string;
  replacesCsid?: string;
}): Promise<QueuedSubmission> {
  if (!formKind || !csid || !record) {
    throw new Error('offlineQueue.enqueue: formKind, csid, and record are required');
  }
  const db = await getDb();
  const now = Date.now();
  const entry: QueuedSubmission = {
    csid,
    form_kind: formKind,
    payload: payload ?? null,
    record,
    status: 'queued',
    retry_count: 0,
    last_error: null,
    created_at: now,
    last_attempt_at: null,
    ...(actorProfileId ? {actor_profile_id: actorProfileId} : {}),
    ...(replacesCsid ? {replaces_csid: replacesCsid} : {}),
  };
  const tx = db.transaction(STORE_SUBMISSIONS, 'readwrite');
  try {
    await linkReplacement(tx.objectStore(STORE_SUBMISSIONS), entry);
    await tx.objectStore(STORE_SUBMISSIONS).put(entry);
    await tx.done;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      /* Already aborted by IndexedDB. */
    }
    await tx.done.catch(() => {});
    throw error;
  }
  return entry;
}

export async function getSubmission(csid: string): Promise<QueuedSubmission | null> {
  const db = await getDb();
  return (await db.get(STORE_SUBMISSIONS, csid)) ?? null;
}

export async function listByFormKind(formKind: string): Promise<QueuedSubmission[]> {
  const db = await getDb();
  return await db.getAllFromIndex(STORE_SUBMISSIONS, 'by_form_kind', formKind);
}

export async function listStuck(formKind: string): Promise<QueuedSubmission[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(STORE_SUBMISSIONS, 'by_form_kind_status', [formKind, 'failed']);
  return all;
}

export async function listQueued(formKind: string): Promise<QueuedSubmission[]> {
  const db = await getDb();
  return await db.getAllFromIndex(STORE_SUBMISSIONS, 'by_form_kind_status', [formKind, 'queued']);
}

export async function markSyncing(csid: string): Promise<QueuedSubmission | null> {
  return await mutate(csid, (entry) => {
    entry.status = 'syncing';
    entry.last_attempt_at = Date.now();
  });
}

/**
 * Mark a submission successfully synced — removes the submission row AND
 * cascades to delete any photo_blobs persisted under the same csid.
 * Auditability lives in Supabase; we don't keep "done" rows on the device.
 *
 * Phase 1D-A: cascade is unconditional. No-photo csids never have blobs;
 * the index lookup just returns an empty list and the delete loop is a
 * no-op.
 */
export async function markSynced(csid: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  await tx.objectStore(STORE_SUBMISSIONS).delete(csid);
  const photoStore = tx.objectStore(STORE_PHOTO_BLOBS);
  const photoIdx = photoStore.index('by_csid');
  const photoKeys = await photoIdx.getAllKeys(csid);
  for (const key of photoKeys) {
    await photoStore.delete(key);
  }
  await tx.done;
}

/**
 * Record a failed attempt. Bumps retry_count. If retries hit MAX_RETRIES,
 * marks the row 'failed' (stuck); otherwise resets to 'queued' for the
 * next sync trigger.
 *
 * Returns the updated entry (or null if the row was already deleted).
 */
export async function markFailed(csid: string, errorMessage?: string | null): Promise<QueuedSubmission | null> {
  return await mutate(csid, (entry) => {
    entry.retry_count = (entry.retry_count ?? 0) + 1;
    entry.last_error = errorMessage ?? null;
    entry.last_attempt_at = Date.now();
    entry.status = entry.retry_count >= MAX_RETRIES ? 'failed' : 'queued';
  });
}

/**
 * Mark a row as stuck immediately, bypassing the retry budget. Used when
 * the sync worker hits a schema/validation error during a background
 * pass — those are real bugs, not transient failures, and silently
 * retrying them 3 times wastes network and delays the operator-visible
 * surface.
 */
export async function markStuckNow(csid: string, errorMessage?: string | null): Promise<QueuedSubmission | null> {
  return await mutate(csid, (entry) => {
    entry.retry_count = MAX_RETRIES;
    entry.last_error = errorMessage ?? null;
    entry.last_attempt_at = Date.now();
    entry.status = 'failed';
  });
}

export const STALE_SYNCING_MS = 30_000;

/**
 * Reset stale 'syncing' rows back to 'queued' so they get picked up
 * by the next sync pass. Handles tab/browser close, reload, or crash
 * mid-sync — rows that flipped to 'syncing' but never reached
 * markSynced / markFailed would otherwise sit in limbo (listQueued
 * filters by status='queued', listStuck by status='failed', so a
 * 'syncing' row appears in neither).
 *
 * Threshold (default 30s) is generous against any plausible single-
 * upsert duration but small enough that an interrupted attempt
 * recovers within one 60s background tick. Fresh in-flight rows
 * within the window stay 'syncing' so concurrent passes (online
 * event + tick + manual button) don't step on each other.
 *
 * Returns the csids that were recovered. Doesn't bump retry_count —
 * an interrupted attempt is not a failed attempt.
 */
export async function recoverStaleSyncing(
  formKind: string,
  {staleAfterMs = STALE_SYNCING_MS}: {staleAfterMs?: number} = {},
): Promise<string[]> {
  const db = await getDb();
  const tx = db.transaction(STORE_SUBMISSIONS, 'readwrite');
  const store = tx.objectStore(STORE_SUBMISSIONS);
  const idx = store.index('by_form_kind_status');
  const all = await idx.getAll([formKind, 'syncing']);
  const cutoff = Date.now() - staleAfterMs;
  const recovered: string[] = [];
  for (const entry of all) {
    if (!entry.last_attempt_at || entry.last_attempt_at <= cutoff) {
      entry.status = 'queued';
      await store.put(entry);
      recovered.push(entry.csid);
    }
  }
  await tx.done;
  return recovered;
}

/**
 * Operator-driven retry from the stuck-submissions modal. Resets retry_count
 * to 0 and flips status back to 'queued'. Last error is cleared so the modal
 * shows fresh telemetry on the next failure.
 */
export async function retrySubmission(csid: string): Promise<QueuedSubmission | null> {
  return await mutate(csid, (entry) => {
    entry.retry_count = 0;
    entry.last_error = null;
    entry.status = 'queued';
  });
}

/**
 * Operator-driven discard. Removes the submission row AND any photo_blobs
 * persisted under the same csid. Caller should warn that the submission
 * is dropped, not deferred. Bucket objects (if any uploaded prior to the
 * stuck state) are NOT auto-deleted — operator/admin cleans up if needed.
 */
export async function discardSubmission(csid: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  const submissions = tx.objectStore(STORE_SUBMISSIONS);
  const discarded = await submissions.get(csid);
  if (discarded?.replaces_csid) {
    const original = await submissions.get(discarded.replaces_csid);
    if (original?.replacement_csid === csid) {
      delete original.replacement_csid;
      original.status = 'failed';
      original.last_error = 'Replacement discarded. Open the original answers to review.';
      await submissions.put(original);
    }
  }
  await submissions.delete(csid);
  const photoStore = tx.objectStore(STORE_PHOTO_BLOBS);
  const photoIdx = photoStore.index('by_csid');
  const photoKeys = await photoIdx.getAllKeys(csid);
  for (const key of photoKeys) {
    await photoStore.delete(key);
  }
  await tx.done;
}

// ============================================================================
// Phase 1D-A — photo_blobs writes + atomic submission+blobs enqueue
// ============================================================================
// The submissions store holds row data only (no Blobs/Files). Photo bytes
// live exclusively in STORE_PHOTO_BLOBS. The two stores are coordinated via
// a single readwrite transaction so a tab/IDB error mid-write can't leave
// half-queued state.

/**
 * Bulk-put prepared photos under one csid. Each entry's primary key is its
 * deterministic path; the by_csid + by_form_kind indices stay in sync.
 */
export async function enqueuePhotoBlobs({
  csid,
  formKind,
  photos,
}: {
  csid: string;
  formKind: string;
  photos?: PreparedPhoto[];
}): Promise<void> {
  if (!csid || !formKind) {
    throw new Error('offlineQueue.enqueuePhotoBlobs: csid + formKind required');
  }
  if (!Array.isArray(photos) || photos.length === 0) return;
  const db = await getDb();
  const tx = db.transaction(STORE_PHOTO_BLOBS, 'readwrite');
  const store = tx.objectStore(STORE_PHOTO_BLOBS);
  for (const p of photos) {
    if (!p.photo_key || !p.path || !p.blob) {
      throw new Error('offlineQueue.enqueuePhotoBlobs: photo_key/path/blob required');
    }
    await store.put({
      key: p.path,
      csid,
      form_kind: formKind,
      photo_key: p.photo_key,
      blob: p.blob,
      mime: p.mime ?? 'image/jpeg',
      size_bytes: p.size_bytes ?? 0,
      name: p.name ?? null,
      captured_at: p.captured_at ?? new Date().toISOString(),
    });
  }
  await tx.done;
}

/**
 * Read all photo_blobs persisted for a csid, in stable photo_key order
 * ('photo-1' < 'photo-2' < ...).
 */
export async function listPhotoBlobsByCsid(csid: string): Promise<PhotoBlobRow[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(STORE_PHOTO_BLOBS, 'by_csid', csid);
  // Sort by the numeric tail of photo_key so 'photo-10' doesn't sort before
  // 'photo-2'. Falls back to lexical compare for non-numeric tails.
  return all.slice().sort((a, b) => {
    const ax = parseInt(String(a.photo_key).replace(/^photo-/, ''), 10);
    const bx = parseInt(String(b.photo_key).replace(/^photo-/, ''), 10);
    if (Number.isFinite(ax) && Number.isFinite(bx) && ax !== bx) return ax - bx;
    return String(a.photo_key).localeCompare(String(b.photo_key));
  });
}

/**
 * Bulk-delete photo_blobs by csid. Used by markSynced + discardSubmission
 * cascades, but exported so the queue worker can also clean up after a
 * synced row that was inserted via the no-photo flat path but somehow
 * has stale blobs (defense-in-depth).
 */
export async function deletePhotoBlobsByCsid(csid: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_PHOTO_BLOBS, 'readwrite');
  const store = tx.objectStore(STORE_PHOTO_BLOBS);
  const idx = store.index('by_csid');
  const keys = await idx.getAllKeys(csid);
  for (const key of keys) {
    await store.delete(key);
  }
  await tx.done;
}

/**
 * Atomic enqueue: writes the submission row AND every photo_blob in a
 * SINGLE readwrite transaction over both stores. Either both land or
 * neither (transaction abort on any error).
 *
 * Status defaults to 'queued' (transient failure path); pass status:'failed'
 * with retry_count:MAX_RETRIES to short-circuit straight to stuck (used
 * for storage 401/403 + row schema-after-upload).
 *
 *   payload — sanitized, NO File/Blob references
 *   record  — daily-row record with photos jsonb (paths only)
 *   photos  — may be empty for no-photo paths, though those typically use
 *             enqueueSubmission directly
 */
export async function enqueueSubmissionWithPhotos({
  csid,
  formKind,
  payload,
  record,
  photos,
  status = 'queued',
  retryCount = 0,
  lastError = null,
  actorProfileId,
  replacesCsid,
}: {
  csid: string;
  formKind: string;
  payload?: unknown;
  record: unknown;
  photos?: PreparedPhoto[];
  status?: 'queued' | 'failed';
  retryCount?: number;
  lastError?: string | null;
  actorProfileId?: string;
  replacesCsid?: string;
}): Promise<void> {
  if (!csid || !formKind || !record) {
    throw new Error('offlineQueue.enqueueSubmissionWithPhotos: csid, formKind, record required');
  }
  // Validate photos UPFRONT before opening the transaction. An auto-commit
  // semantics quirk in IDB means a partial loop after a successful put may
  // still commit the prior puts even if the function throws. Pre-validation
  // makes the atomicity contract trivially correct: we only open the
  // transaction once the entire input is verified.
  const photosList = Array.isArray(photos) ? photos : [];
  for (const p of photosList) {
    if (!p || !p.photo_key || !p.path || !p.blob) {
      throw new Error('offlineQueue.enqueueSubmissionWithPhotos: photo_key/path/blob required on every entry');
    }
  }
  const db = await getDb();
  const now = Date.now();
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  const entry: QueuedSubmission = {
    csid,
    form_kind: formKind,
    payload: payload ?? null,
    record,
    status,
    retry_count: retryCount,
    last_error: lastError,
    created_at: now,
    last_attempt_at: status === 'queued' ? null : now,
    ...(actorProfileId ? {actor_profile_id: actorProfileId} : {}),
    ...(replacesCsid ? {replaces_csid: replacesCsid} : {}),
  };
  try {
    await linkReplacement(tx.objectStore(STORE_SUBMISSIONS), entry);
    await tx.objectStore(STORE_SUBMISSIONS).put(entry);
    if (photosList.length > 0) {
      const photoStore = tx.objectStore(STORE_PHOTO_BLOBS);
      for (const p of photosList) {
        await photoStore.put({
          key: p.path,
          csid,
          form_kind: formKind,
          photo_key: p.photo_key,
          blob: p.blob,
          mime: p.mime ?? 'image/jpeg',
          size_bytes: p.size_bytes ?? 0,
          name: p.name ?? null,
          captured_at: p.captured_at ?? new Date().toISOString(),
        });
      }
    }
    await tx.done;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      /* Already aborted by IndexedDB. */
    }
    await tx.done.catch(() => {});
    throw error;
  }
}

async function linkReplacement(
  store: {get(key: string): Promise<QueuedSubmission | undefined>; put(value: QueuedSubmission): Promise<unknown>},
  entry: QueuedSubmission,
) {
  if (entry.actor_profile_id && (await store.get(entry.csid))) {
    throw new Error('This queued identity already contains saved answers. Open it to continue.');
  }
  if (!entry.replaces_csid) return;
  const original = await store.get(entry.replaces_csid);
  if (!original || original.csid === entry.csid || original.form_kind !== entry.form_kind) {
    throw new Error('The original queued report is no longer available. Open the pending report again.');
  }
  if (!entry.actor_profile_id || (original.actor_profile_id && original.actor_profile_id !== entry.actor_profile_id)) {
    throw new Error('Sign in with the account that queued this report.');
  }
  if (original.replacement_csid && original.replacement_csid !== entry.csid) {
    throw new Error('A replacement is already pending. Open that report to continue.');
  }
  await store.put({...original, replacement_csid: entry.csid});
}

// Only a positively verified flat-report write may use this acknowledgement.
// Keep the complete recovery chain until the new intended report has committed.
export async function acknowledgeQueuedReport(csid: string, actorProfileId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  const submissions = tx.objectStore(STORE_SUBMISSIONS);
  const photos = tx.objectStore(STORE_PHOTO_BLOBS);
  let next: string | undefined = csid;
  const seen = new Set<string>();
  try {
    while (next) {
      if (seen.has(next)) throw new Error('Recovery links need help. The queued reports were retained.');
      seen.add(next);
      const entry = await submissions.get(next);
      if (!entry) break;
      if (entry.actor_profile_id && entry.actor_profile_id !== actorProfileId) {
        throw new Error('Queued report account changed; recovery was retained.');
      }
      await submissions.delete(next);
      for (const key of await photos.index('by_csid').getAllKeys(next)) await photos.delete(key);
      next = entry.replaces_csid;
    }
    await tx.done;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      /* Already aborted by IndexedDB. */
    }
    await tx.done.catch(() => {});
    throw error;
  }
}

async function mutate(csid: string, fn: (entry: QueuedSubmission) => void): Promise<QueuedSubmission | null> {
  const db = await getDb();
  const tx = db.transaction(STORE_SUBMISSIONS, 'readwrite');
  const store = tx.objectStore(STORE_SUBMISSIONS);
  const entry = await store.get(csid);
  if (!entry) {
    await tx.done;
    return null;
  }
  fn(entry);
  await store.put(entry);
  await tx.done;
  return entry;
}

// ============================================================================
// Cattle Log (form_kind 'cattle_log') — create-only offline queue extensions
// ============================================================================
// Reuses the two existing stores (store names + DB_VERSION are pinned by the
// IndexedDB boundary static guard, so no new store). Submission rows live in
// `submissions` under form_kind 'cattle_log' with three extra fields the
// generic flow doesn't carry:
//   - uploadedPaths: storage paths confirmed uploaded to comment-photos.
//     Persisted after EACH successful attachment upload so a replay never
//     re-sends finished bytes (partial-replay resume).
//   - errorClass: 'transient' | 'ambiguous_tag' | 'mention_invalid' |
//     'validation' (classifyCattleLogError vocabulary).
//   - errorMessage: human-readable detail for the needs-attention UI.
// Attachment bytes live in `photo_blobs`, keyed by the deterministic
// comment-photos path 'cattle.log/cattle-log/<entryId>/<index>-<name>'.
//
// Status flow for cattle_log differs from the retry-budget forms:
//   queued          → ready for the next replay pass. Transient failures
//                     return here with NO retry cap — submit_cattle_log_entry
//                     is replay-idempotent via p_id, so retrying forever is
//                     safe and matches the create-only offline contract.
//   syncing         → in flight (recoverStaleSyncing applies as usual).
//   needs_attention → deterministic failure (ambiguous_tag / mention_invalid
//                     / validation). Operator must Retry or Discard. This is
//                     the cattle_log parallel of 'failed' for other forms —
//                     listQueued() skips it, so replay never picks it up
//                     until setCattleLogOutcome flips it back to 'queued'.
// Synced rows are deleted (markSynced cascade), same as every other form.

export const CATTLE_LOG_FORM_KIND = 'cattle_log';

// The two statuses setCattleLogOutcome accepts. Typed as the pair rather than
// as SubmissionStatus: 'syncing' and 'failed' are reachable elsewhere in the
// flow but are NOT valid outcomes of a replay pass, and the runtime throw below
// says so.
export type CattleLogOutcomeStatus = 'queued' | 'needs_attention';

const CATTLE_LOG_OUTCOME_STATUSES: readonly CattleLogOutcomeStatus[] = ['queued', 'needs_attention'];

// One cattle-log attachment as the caller hands it over. `key` is the FULL
// deterministic comment-photos path and becomes the photo_blobs primary key;
// `photo_key` is the per-entry index. Both, plus the bytes, are required — the
// pre-validation loop below rejects a row missing any of them.
export type CattleLogAttachment = {
  key: string;
  photo_key: string;
  blob: Blob;
  mime?: string;
  size_bytes?: number;
  name?: string | null;
  captured_at?: string;
};

/**
 * Atomic enqueue for a cattle log entry: submission row + attachment blobs
 * in a SINGLE readwrite transaction over both stores (either both land or
 * neither). `record` is null for this form_kind — replay rebuilds the RPC
 * args from `payload` (the payload IS the deterministic call shape).
 *
 *   csid    — the cattle log entry id ('cl-…'); doubles as the idempotency key
 *             (submit_cattle_log_entry p_id).
 *   payload — {id, body, mentions, isIssue, calfNotes, attachments:
 *             [{key, name, mime, size, is_image, captured_at}]}. NO File/Blob
 *             references (submissions-store contract).
 *   attachments — blob rows; `key` is the full deterministic comment-photos path.
 */
export async function enqueueCattleLogSubmission({
  csid,
  payload,
  attachments = [],
}: {
  csid: string;
  payload: unknown;
  attachments?: CattleLogAttachment[];
}): Promise<QueuedSubmission> {
  if (!csid || !payload) {
    throw new Error('offlineQueue.enqueueCattleLogSubmission: csid + payload required');
  }
  // Pre-validate before opening the transaction (same rationale as
  // enqueueSubmissionWithPhotos: IDB auto-commit semantics make mid-loop
  // throws unreliable for atomicity; only open the tx on verified input).
  const blobRows = Array.isArray(attachments) ? attachments : [];
  for (const a of blobRows) {
    if (!a || !a.key || !a.photo_key || !a.blob) {
      throw new Error('offlineQueue.enqueueCattleLogSubmission: key/photo_key/blob required on every attachment');
    }
  }
  const db = await getDb();
  const now = Date.now();
  const entry: QueuedSubmission = {
    csid,
    form_kind: CATTLE_LOG_FORM_KIND,
    payload,
    record: null,
    status: 'queued',
    retry_count: 0,
    last_error: null,
    errorClass: null,
    errorMessage: null,
    uploadedPaths: [],
    created_at: now,
    last_attempt_at: null,
  };
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  await tx.objectStore(STORE_SUBMISSIONS).put(entry);
  if (blobRows.length > 0) {
    const photoStore = tx.objectStore(STORE_PHOTO_BLOBS);
    for (const a of blobRows) {
      await photoStore.put({
        key: a.key,
        csid,
        form_kind: CATTLE_LOG_FORM_KIND,
        photo_key: a.photo_key,
        blob: a.blob,
        mime: a.mime ?? 'application/octet-stream',
        size_bytes: a.size_bytes ?? 0,
        name: a.name ?? null,
        captured_at: a.captured_at ?? new Date().toISOString(),
      });
    }
  }
  await tx.done;
  return entry;
}

/**
 * Persist one confirmed upload path onto the entry. Called after EACH
 * successful (or duplicate-as-success) storage upload during replay so an
 * interruption between uploads resumes exactly where it left off.
 * Idempotent: appending an already-recorded path is a no-op.
 */
export async function appendCattleLogUploadedPath(csid: string, path: string): Promise<QueuedSubmission | null> {
  if (!path) {
    throw new Error('offlineQueue.appendCattleLogUploadedPath: path required');
  }
  return await mutate(csid, (entry) => {
    const list = Array.isArray(entry.uploadedPaths) ? entry.uploadedPaths : [];
    if (!list.includes(path)) list.push(path);
    entry.uploadedPaths = list;
  });
}

/**
 * Record a replay outcome (or an operator retry) on a cattle log entry.
 *   - failure: setCattleLogOutcome(csid, {status, errorClass, errorMessage})
 *     where status is 'queued' (transient — retried automatically) or
 *     'needs_attention' (deterministic — waits for operator Retry/Discard).
 *   - operator retry: setCattleLogOutcome(csid, {status: 'queued'}) clears
 *     errorClass/errorMessage so the row re-enters the replay pass with
 *     fresh telemetry. uploadedPaths is intentionally NOT cleared — retry
 *     must skip uploads that already landed.
 * Success has no outcome call: markSynced deletes the row + blob cascade.
 */
export async function setCattleLogOutcome(
  csid: string,
  {
    status,
    errorClass = null,
    errorMessage = null,
  }: {status: CattleLogOutcomeStatus; errorClass?: string | null; errorMessage?: string | null},
): Promise<QueuedSubmission | null> {
  if (!CATTLE_LOG_OUTCOME_STATUSES.includes(status)) {
    throw new Error(`offlineQueue.setCattleLogOutcome: invalid status '${status}'`);
  }
  return await mutate(csid, (entry) => {
    entry.status = status;
    entry.errorClass = errorClass;
    entry.errorMessage = errorMessage;
    // Mirror into the generic telemetry field so any form-agnostic tooling
    // (stale-row inspection, debugging) sees the same message.
    entry.last_error = errorMessage;
    if (errorClass != null) entry.last_attempt_at = Date.now();
  });
}

// ============================================================================
// Local drafts — single-transaction compare-and-swap
// ============================================================================
// A local-draft kind (never registered, never retried or flushed) keeps ONE
// row per (actor, form) under a deterministic csid. Each write names the
// stamp it expects to replace (null: no row yet) and a synchronous live-owner
// check. Both are decided inside the SAME readwrite transaction as the put:
// a read in one transaction and a write in another would let a stale or
// foreign revision land in between.
//
// This does not cancel a put already issued before an owner switch. That put
// commits under the actor key it captured, never under another actor's, and
// it can never replace a revision it did not expect.
export type LocalDraftStamp = {mountId: string; revision: number};
export type LocalDraftWriteResult =
  | {status: 'written'}
  | {status: 'conflict'; stamp: LocalDraftStamp | null}
  | {status: 'not_current'};
export type LocalDraftDeleteResult = 'deleted' | 'kept' | 'absent' | 'not_current';

function localDraftStamp(row: QueuedSubmission): LocalDraftStamp | null {
  const record = row.record as {stamp?: {mountId?: unknown; revision?: unknown}} | null;
  const stamp = record && typeof record === 'object' ? record.stamp : null;
  if (!stamp || typeof stamp.mountId !== 'string' || !Number.isSafeInteger(stamp.revision)) return null;
  return {mountId: stamp.mountId, revision: stamp.revision as number};
}

function sameLocalDraftStamp(a: LocalDraftStamp | null, b: LocalDraftStamp | null): boolean {
  return !!a && !!b && a.mountId === b.mountId && a.revision === b.revision;
}

function assertLocalDraftOwner(row: QueuedSubmission | undefined, formKind: string, actorProfileId: string) {
  if (row && (row.form_kind !== formKind || row.actor_profile_id !== actorProfileId)) {
    throw new Error('This draft key holds other saved answers. They were retained.');
  }
}

async function inLocalDraftTx<T>(work: (store: SubmissionsStore) => Promise<T>): Promise<T> {
  const db = await getDb();
  const tx = db.transaction(STORE_SUBMISSIONS, 'readwrite');
  try {
    const result = await work(tx.objectStore(STORE_SUBMISSIONS));
    await tx.done;
    return result;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      /* Already aborted by IndexedDB. */
    }
    await tx.done.catch(() => {});
    throw error;
  }
}

type SubmissionsStore = {
  get(key: string): Promise<QueuedSubmission | undefined>;
  put(value: QueuedSubmission): Promise<unknown>;
  delete(key: string): Promise<unknown>;
};

/**
 * Write `payload` as the local draft at `csid` only when the stored row's stamp
 * is `expectStamp` (null: only when no row exists) AND `stillCurrent()` is true
 * at that moment, then stamp it `stamp`. `stillCurrent` must be synchronous: it
 * runs between the get and the put, inside the transaction.
 */
export async function casLocalDraft({
  formKind,
  csid,
  actorProfileId,
  expectStamp,
  stamp,
  payload,
  lastError,
  stillCurrent,
}: {
  formKind: string;
  csid: string;
  actorProfileId: string;
  expectStamp: LocalDraftStamp | null;
  stamp: LocalDraftStamp;
  payload: unknown;
  lastError: string;
  stillCurrent: () => boolean;
}): Promise<LocalDraftWriteResult> {
  if (!formKind || !csid || !actorProfileId || !stamp) {
    throw new Error('offlineQueue.casLocalDraft: formKind, csid, actorProfileId and stamp are required');
  }
  return inLocalDraftTx(async (store) => {
    const existing = await store.get(csid);
    assertLocalDraftOwner(existing, formKind, actorProfileId);
    const current = existing ? localDraftStamp(existing) : null;
    const expected = existing ? sameLocalDraftStamp(current, expectStamp) : expectStamp === null;
    if (!expected) return {status: 'conflict', stamp: current} as const;
    if (!stillCurrent()) return {status: 'not_current'} as const;
    const now = Date.now();
    await store.put({
      csid,
      form_kind: formKind,
      payload,
      record: {local_draft: true, stamp},
      status: 'failed',
      retry_count: MAX_RETRIES,
      last_error: lastError,
      created_at: existing?.created_at ?? now,
      last_attempt_at: null,
      actor_profile_id: actorProfileId,
    });
    return {status: 'written'} as const;
  });
}

/** Remove the local draft at `csid` only while it still carries `stamp`. */
export async function deleteLocalDraftIfStamp({
  formKind,
  csid,
  actorProfileId,
  stamp,
  stillCurrent,
}: {
  formKind: string;
  csid: string;
  actorProfileId: string;
  stamp: LocalDraftStamp;
  stillCurrent: () => boolean;
}): Promise<LocalDraftDeleteResult> {
  if (!formKind || !csid || !actorProfileId || !stamp) {
    throw new Error('offlineQueue.deleteLocalDraftIfStamp: formKind, csid, actorProfileId and stamp are required');
  }
  return inLocalDraftTx(async (store) => {
    const existing = await store.get(csid);
    if (!existing) return 'absent' as const;
    assertLocalDraftOwner(existing, formKind, actorProfileId);
    if (!sameLocalDraftStamp(localDraftStamp(existing), stamp)) return 'kept' as const;
    if (!stillCurrent()) return 'not_current' as const;
    await store.delete(csid);
    return 'deleted' as const;
  });
}

/** The stamp a stored local draft carries, for a caller that read it with getSubmission. */
export function localDraftStampOf(row: QueuedSubmission | null | undefined): LocalDraftStamp | null {
  return row ? localDraftStamp(row) : null;
}

// ============================================================================
// Local drafts with photos — single-transaction compare-and-swap
// ============================================================================
// The photo-capable counterpart of casLocalDraft/deleteLocalDraftIfStamp for
// the Hub's review-only draft (answers AND original photo bytes). ONE
// readwrite transaction spans submissions + photo_blobs, so a draft revision
// and its exact original bytes commit together or not at all — a tab/IDB
// error mid-write can never leave half a draft.
//
// This path is STRICTLY for review-only local draft kinds: a row that already
// exists at the key must carry local_draft=true and sit failed+MAX_RETRIES
// (never registered for submission). A real queued report at the key is
// refused and retained, never overwritten, retried, or discarded.
export type PreparedDraftPhoto = {
  photo_key: string;
  path: string;
  blob: Blob;
  mime: string;
  size_bytes: number;
  name: string | null;
  captured_at: string;
};

type DraftPhotoBlobsStore = {
  get(key: string): Promise<PhotoBlobRow | undefined>;
  index(name: 'by_csid'): {getAllKeys(csid: string): Promise<string[]>};
  put(value: PhotoBlobRow): Promise<unknown>;
  delete(key: string): Promise<unknown>;
};

/** Thrown INSIDE the transaction when the caller's currency check fails at an
 * awaited boundary: the throw aborts the transaction, so IndexedDB rolls back
 * every write already made — a stale owner can never commit a partial
 * mutation. `saveHubDraftCas` maps this to its truthful not-current result. */
export class DraftNotCurrentAbort extends Error {
  readonly notCurrent = true;
  constructor(message: string) {
    super(message);
    this.name = 'DraftNotCurrentAbort';
  }
}

/** Every field the photo rows store is required up front: a draft revision is
 * only ever written with its complete original bytes and metadata. */
function assertDraftPhotos(photos: ReadonlyArray<PreparedDraftPhoto>) {
  const seen = new Set<string>();
  for (const photo of photos) {
    if (!photo.photo_key || !photo.path || !photo.blob || !photo.captured_at) {
      throw new Error('offlineQueue.casLocalDraftWithPhotos: photo_key, path, blob and captured_at are required');
    }
    if (typeof photo.size_bytes !== 'number' || !Number.isSafeInteger(photo.size_bytes) || photo.size_bytes < 0) {
      throw new Error('offlineQueue.casLocalDraftWithPhotos: photo size_bytes must be a safe non-negative integer');
    }
    if (seen.has(photo.path)) throw new Error('offlineQueue.casLocalDraftWithPhotos: duplicate photo path');
    seen.add(photo.path);
  }
}

/** The kind guard on top of ownership: an existing row must be the SAME
 * actor's review-only local draft. Anything else — another form kind, another
 * actor, or a real queued submission — is refused and retained. */
function assertLocalDraftKind(existing: QueuedSubmission | undefined, formKind: string, actorProfileId: string) {
  assertLocalDraftOwner(existing, formKind, actorProfileId);
  if (!existing) return;
  const record = existing.record as {local_draft?: unknown} | null;
  const reviewOnly =
    record !== null &&
    typeof record === 'object' &&
    record.local_draft === true &&
    existing.status === 'failed' &&
    (existing.retry_count ?? 0) >= MAX_RETRIES;
  if (!reviewOnly) {
    throw new Error('This key holds a real queued submission. It was retained.');
  }
}

async function inLocalDraftPhotosTx<T>(
  work: (submissions: SubmissionsStore, photos: DraftPhotoBlobsStore) => Promise<T>,
): Promise<T> {
  const db = await getDb();
  const tx = db.transaction([STORE_SUBMISSIONS, STORE_PHOTO_BLOBS], 'readwrite');
  try {
    const result = await work(tx.objectStore(STORE_SUBMISSIONS), tx.objectStore(STORE_PHOTO_BLOBS));
    await tx.done;
    return result;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      /* Already aborted by IndexedDB. */
    }
    await tx.done.catch(() => {});
    throw error;
  }
}

/**
 * Write `payload` plus the complete set of original photo bytes as the local
 * draft at `csid` only when the stored row's stamp is `expectStamp` (null:
 * only when no row exists) AND `stillCurrent()` is true inside the
 * transaction. Superseded photo rows of THIS draft (keys under its csid that
 * the new set does not name) are removed in the same transaction; every
 * kept/rewritten photo keeps the caller's original bytes, name, mime, size
 * and captured_at. A conflict, a stale owner, or any error leaves the prior
 * row and its photos exactly as they were.
 */
export async function casLocalDraftWithPhotos({
  formKind,
  csid,
  actorProfileId,
  expectStamp,
  stamp,
  payload,
  lastError,
  photos,
  stillCurrent,
}: {
  formKind: string;
  csid: string;
  actorProfileId: string;
  expectStamp: LocalDraftStamp | null;
  stamp: LocalDraftStamp;
  payload: unknown;
  lastError: string;
  photos: ReadonlyArray<PreparedDraftPhoto>;
  stillCurrent: () => boolean;
}): Promise<LocalDraftWriteResult> {
  if (!formKind || !csid || !actorProfileId || !stamp) {
    throw new Error('offlineQueue.casLocalDraftWithPhotos: formKind, csid, actorProfileId and stamp are required');
  }
  assertDraftPhotos(photos);
  return inLocalDraftPhotosTx(async (submissions, photoStore) => {
    const existing = await submissions.get(csid);
    assertLocalDraftKind(existing, formKind, actorProfileId);
    const current = existing ? localDraftStamp(existing) : null;
    const expected = existing ? sameLocalDraftStamp(current, expectStamp) : expectStamp === null;
    if (!expected) return {status: 'conflict', stamp: current} as const;
    if (!stillCurrent()) return {status: 'not_current'} as const;
    // photo_blobs' primary key is GLOBAL, so every incoming photo key is
    // ownership-checked in THIS transaction before anything is written: an
    // existing key owned by another draft or a real report (any kind) must
    // never be rewritten. A collision throws, which aborts the whole
    // transaction — our prior row and photos, and theirs, stay untouched.
    for (const photo of photos) {
      const existingPhoto = await photoStore.get(photo.path);
      if (existingPhoto && (existingPhoto.csid !== csid || existingPhoto.form_kind !== formKind)) {
        throw new Error('This photo belongs to other saved work. Nothing was changed.');
      }
    }
    // The ownership reads were awaited: the caller may have gone stale during
    // them. From here on a stale owner ABORTS (rolls back) instead of
    // returning — a plain return after the writes below would commit.
    if (!stillCurrent()) {
      throw new DraftNotCurrentAbort('offlineQueue.casLocalDraftWithPhotos: the draft is no longer current');
    }
    const now = Date.now();
    await submissions.put({
      csid,
      form_kind: formKind,
      payload,
      record: {local_draft: true, stamp, content_hash: (payload as {contentHash?: string} | null)?.contentHash ?? null},
      status: 'failed',
      retry_count: MAX_RETRIES,
      last_error: lastError,
      created_at: existing?.created_at ?? now,
      last_attempt_at: null,
      actor_profile_id: actorProfileId,
    });
    const keep = new Set(photos.map((photo) => photo.path));
    const oldKeys = await photoStore.index('by_csid').getAllKeys(csid);
    // Boundary after the awaited index read...
    if (!stillCurrent()) {
      throw new DraftNotCurrentAbort('offlineQueue.casLocalDraftWithPhotos: the draft is no longer current');
    }
    for (const key of oldKeys) {
      if (!keep.has(key)) await photoStore.delete(key);
    }
    for (const photo of photos) {
      await photoStore.put({
        key: photo.path,
        csid,
        form_kind: formKind,
        photo_key: photo.photo_key,
        blob: photo.blob,
        mime: photo.mime,
        size_bytes: photo.size_bytes,
        name: photo.name,
        captured_at: photo.captured_at,
      });
    }
    // ...and the boundary that actually matters: the deletes/puts above were
    // awaited, so the LAST decision before commit belongs to the caller's
    // currency check. A flip during any awaited write lands here and the
    // throw rolls the ENTIRE revision back.
    if (!stillCurrent()) {
      throw new DraftNotCurrentAbort('offlineQueue.casLocalDraftWithPhotos: the draft is no longer current');
    }
    return {status: 'written'} as const;
  });
}

/**
 * Remove the local draft at `csid` AND all of its photo bytes only while the
 * row still carries `stamp` and `stillCurrent()` holds inside the
 * transaction. 'kept' means a newer revision owns the key: nothing is
 * deleted. A real queued submission is never eligible.
 */
export async function deleteLocalDraftWithPhotosIfStamp({
  formKind,
  csid,
  actorProfileId,
  stamp,
  stillCurrent,
}: {
  formKind: string;
  csid: string;
  actorProfileId: string;
  stamp: LocalDraftStamp;
  stillCurrent: () => boolean;
}): Promise<LocalDraftDeleteResult> {
  if (!formKind || !csid || !actorProfileId || !stamp) {
    throw new Error(
      'offlineQueue.deleteLocalDraftWithPhotosIfStamp: formKind, csid, actorProfileId and stamp are required',
    );
  }
  return inLocalDraftPhotosTx(async (submissions, photoStore) => {
    const existing = await submissions.get(csid);
    if (!existing) return 'absent' as const;
    assertLocalDraftKind(existing, formKind, actorProfileId);
    if (!sameLocalDraftStamp(localDraftStamp(existing), stamp)) return 'kept' as const;
    if (!stillCurrent()) return 'not_current' as const;
    await submissions.delete(csid);
    const photoKeys = await photoStore.index('by_csid').getAllKeys(csid);
    // Boundary after the awaited index read...
    if (!stillCurrent()) {
      throw new DraftNotCurrentAbort('offlineQueue.deleteLocalDraftWithPhotosIfStamp: the draft is no longer current');
    }
    for (const key of photoKeys) {
      await photoStore.delete(key);
    }
    // ...and the last decision before commit: a flip during the awaited
    // deletes rolls the row and byte removals back entirely.
    if (!stillCurrent()) {
      throw new DraftNotCurrentAbort('offlineQueue.deleteLocalDraftWithPhotosIfStamp: the draft is no longer current');
    }
    return 'deleted' as const;
  });
}
